import { ReactElement, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Icon,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  Loader,
  Table,
  TableCell,
} from "@screencloud/screencloud-ui-components";
import { ChannelStore, EditorStore } from "../../types";
import { selectChannel, startDelete, startRename } from "../../actions";
import { RootState } from "../../reducers";
import "./StreamList.scss";
import LiveStreamLogo from "../../assets/live-stream-logo.svg";

const LABEL_TEXT = {
  STREAM_NAME_HEADER: "Your Streaming Channels",
  STREAM_CREATED_HEADER: "Created",
  STREAM_LAST_USED_HEADER: "Last Used",
  STREAM_MENU_DELETE: "Delete",
  STREAM_MENU_RENAME: "Rename",
};

interface StreamRowMenuProps {
  onDeleteClick: (channelId: string) => void;
  onRenameClick: (channelId: string) => void;
  onMenuClick: () => void;
  channelId: string;
}

interface RowStreamProps {
  channelId: string;
  name: string;
  spaceId: string;
  lastBroadcast: number;
  createdAt: number;
  onDeleteClick: () => void;
  onRenameClick: () => void;
  onRowClick: () => void;
  onMenuClick: () => void;
  loading: boolean;
}

interface RowIconProps {
  key: string;
  onCellClick: () => void;
}

interface RowNameProps {
  name: string;
  onCellClick: () => void;
}

interface RowLastUsedProps {
  key: string;
  date: number | undefined;
  onCellClick: () => void;
}

const HeaderRow = [
  {
    content: "",
    key: "icon",
    className:
      "channel-header column two wide mobile one wide tablet one wide computer",
  },
  {
    content: LABEL_TEXT.STREAM_NAME_HEADER.toUpperCase(),
    key: "channel",
    className:
      "channel-header column eight wide mobile eight wide tablet eight wide computer",
  },
  {
    content: LABEL_TEXT.STREAM_LAST_USED_HEADER.toUpperCase(),
    key: "lastUsed",
    className:
      "channel-header column mobile hidden three wide tablet three wide computer",
  },
  {
    content: LABEL_TEXT.STREAM_CREATED_HEADER.toUpperCase(),
    key: "created",
    className:
      "channel-header column mobile hidden three wide tablet three wide computer",
  },
  {
    content: "",
    key: "menu",
    className:
      "channel-header column one wide mobile one wide tablet one wide computer",
  },
];

const RowIcon = (props: RowIconProps): ReactElement<null> => {
  const { onCellClick } = props;
  return (
    <TableCell
      textAlign={"center"}
      id={"row-logo"}
      onClick={onCellClick}
      className={"column two wide mobile one wide tablet one wide computer"}
    >
      <img className={"live-stream-icon"} src={LiveStreamLogo} alt="Logo" />
    </TableCell>
  );
};

const RowName = (props: RowNameProps): ReactElement<RowNameProps> => {
  const { onCellClick } = props;

  return (
    <TableCell
      id="name"
      onClick={onCellClick}
      className={
        "column twelve wide mobile eight wide tablet eight wide computer"
      }
    >
      {props.name}
    </TableCell>
  );
};

const RowDate = (props: RowLastUsedProps): ReactElement<RowLastUsedProps> => {
  const { date, onCellClick } = props;

  const lastBroadcast = date
    ? new Date(date).toLocaleDateString("en-gb", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  return (
    <TableCell
      id="lastused-cell"
      className={"column mobile hidden three wide tablet three wide computer"}
      onClick={onCellClick}
    >
      {lastBroadcast}
    </TableCell>
  );
};

const RowMenu = (
  props: StreamRowMenuProps
): ReactElement<StreamRowMenuProps> => {
  const { channelId, onDeleteClick, onRenameClick, onMenuClick } = props;

  const editor: EditorStore = useSelector((state: RootState) => state.editor);

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const actionInProgress = editor.actionInProgress;

    setProcessing(actionInProgress === channelId);
  }, [editor, channelId]);

  const renameClick = (): void => {
    onRenameClick(channelId);
  };

  const deleteClick = (): void => {
    onDeleteClick(channelId);
  };

  return (
    <TableCell
      className={
        "stream-list_rows_row_menu column two wide mobile one wide tablet one wide computer"
      }
      id="row-menu"
      verticalAlign={"middle"}
    >
      {processing ? (
        <Loader inline="centered" size="tiny" active />
      ) : (
        <Dropdown icon={<Icon name="dot" />} onClick={onMenuClick}>
          <DropdownMenu>
            <DropdownItem
              className={"stream-menu-list"}
              onClick={renameClick}
              icon={<Icon name="edit" />}
              text={LABEL_TEXT.STREAM_MENU_RENAME}
            />
            <DropdownDivider />
            <DropdownItem
              className={"stream-menu-list delete"}
              onClick={deleteClick}
              icon={<Icon name="warning" />}
              text={LABEL_TEXT.STREAM_MENU_DELETE}
            />
          </DropdownMenu>
        </Dropdown>
      )}
    </TableCell>
  );
};

const renderBodyRow = (
  {
    name,
    createdAt,
    lastBroadcast,
    channelId,
    onRowClick,
    onMenuClick,
    onDeleteClick,
    onRenameClick,
  }: RowStreamProps,
  i: number
): { key: string; cells: JSX.Element[] } => ({
  key: `row-${i}`,
  cells: [
    <RowIcon key={`row-icn${i}`} onCellClick={onRowClick} />,
    <RowName key={`row-name${i}`} name={name} onCellClick={onRowClick} />,
    <RowDate
      key={`row-lu${i}`}
      date={lastBroadcast}
      onCellClick={onRowClick}
    />,
    <RowDate key={`row-cr${i}`} date={createdAt} onCellClick={onRowClick} />,
    <RowMenu
      key={`row-mu${i}`}
      channelId={channelId}
      onDeleteClick={onDeleteClick}
      onMenuClick={onMenuClick}
      onRenameClick={onRenameClick}
    />,
  ],
});

const StreamList = (): ReactElement<void> => {
  const channels: ChannelStore = useSelector(
    (state: RootState) => state.channels
  );

  const dispatch = useDispatch();

  const [menuVisibleId, setMenuVisibleId] = useState<string | null>(null);
  const [allowSelection, setAllowSelection] = useState<boolean>(true);

  const onMenuClick = (channelId: string): void => {
    if (menuVisibleId && menuVisibleId === channelId) {
      setMenuVisibleId(null);
    } else {
      setMenuVisibleId(channelId);
      setAllowSelection(false); // prevent selection of row when rename/delete menu visible
    }
  };

  const onRenameClick = (channelId: string): void => {
    const filteredChannels = channels.available.filter(
      (channel) => channel.channelId === channelId
    );
    if (filteredChannels.length > 0) {
      const selectedChannel = filteredChannels[0];
      dispatch(startRename(selectedChannel));
    }
  };

  const onDeleteClick = (channelId: string): void => {
    const filteredChannels = channels.available.filter(
      (channel) => channel.channelId === channelId
    );
    if (filteredChannels.length > 0) {
      const selectedChannel = filteredChannels[0];
      dispatch(startDelete(selectedChannel));
    }
  };

  const checkRowSelection = (channelId: string): void => {
    // stops from selecting row when click off of the rename/delete menu
    if (!allowSelection) {
      setAllowSelection(true);
    } else {
      const filteredChannels = channels.available.filter(
        (channel) => channel.channelId === channelId
      );
      if (filteredChannels.length > 0) {
        const selectedChannel = filteredChannels[0];
        dispatch(selectChannel(selectedChannel));
      }
    }
  };

  const data = channels.available.map(
    ({ name, channelId, createdAt, lastBroadcast }) => {
      return {
        name,
        channelId,
        createdAt,
        lastBroadcast,
        onDeleteClick: onDeleteClick,
        onRenameClick: onRenameClick,
        onMenuClick: () => onMenuClick(channelId),
        onRowClick: () => checkRowSelection(channelId),
      };
    }
  );
  return (
    <div className={"stream-list"}>
      <Table
        basic="very"
        selectable
        headerRow={HeaderRow}
        renderBodyRow={renderBodyRow}
        tableData={data}
      />
    </div>
  );
};

export default StreamList;
